// @file Vuex Store module for Reactions
/* eslint-disable no-param-reassign */
import { useNativeAppStore } from '@@/pinia/native_app'
import { JsonAPIResource, JsonAPIResponse, Reaction } from '@padlet/arvo'
import appCan from 'bits/app_can'
import { captureException, captureFetchException } from 'bits/error_tracker'
import { ACCESS_WITHDRAWN } from 'bits/snackbar_helper'
import { openPostReactionDialog } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import PadletApi from 'surface/padlet_api'
import type { AccumulatedReactions, Id } from 'types'
import { Module } from 'vuex'
import { reconcileReactionsAfterFetch } from 'vuexstore/helpers/reaction'
import { RootState } from 'vuexstore/surface/types'

type ReactionId = string | number

interface ReactionState {
  isIntialReactionsLoadDone: boolean
  reactionEntities: { [id: string]: any }
  pendingReactionIds: ReactionId[]
  postIdBeingReactedTo: Id | null
  reactionDropdownTop: number | null
  reactionDropdownLeft: number | null
  reactionDropdownWidth: number | null
  accumulatedReactionsByWishId: AccumulatedReactions
}

const ReactionModule: Module<ReactionState, RootState> = {
  namespaced: true,
  state: {
    isIntialReactionsLoadDone: false,
    reactionEntities: {},
    pendingReactionIds: [],
    postIdBeingReactedTo: null,
    reactionDropdownTop: null,
    reactionDropdownLeft: null,
    reactionDropdownWidth: null,
    accumulatedReactionsByWishId: {},
  },
  getters: {
    // Do not expose reaction data for display until the initial paginated data load is done
    reactions(state, getters): Reaction[] {
      if (!getters.isIntialReactionsLoadDone) {
        return []
      }
      return Object.values(state.reactionEntities)
    },
    reactionIds(state, getters): string[] {
      if (!getters.isIntialReactionsLoadDone) {
        return []
      }
      return Object.keys(state.reactionEntities)
    },
    reactionIdsByPost(state, getters, rootState, rootGetters): { [id: number]: ReactionId[] } {
      const reactionsByPost = {}
      try {
        getters.reactionIds.forEach((rid: ReactionId): void => {
          const r = state.reactionEntities[rid]
          reactionsByPost[r.wish_id] = reactionsByPost[r.wish_id] || []
          if (r.reaction_type === rootGetters.reactionData.type) {
            reactionsByPost[r.wish_id].push(rid)
          }
        })
      } catch (e) {
        captureException(e, {
          context: {
            reactionData: JSON.stringify(rootGetters.reactionData),
            isPreviewing: rootGetters['settings/isPreviewing'],
            displayAttrs: JSON.stringify(rootGetters.displayAttrs),
            wall: JSON.stringify(rootState.wall),
          },
        })
      }

      return reactionsByPost
    },
    hasReactions(state, getters): boolean {
      return getters.reactionIds.length > 0
    },
    maxGrade(state, getters, rootState, rootGetters): number {
      if (!rootGetters.isReactable) return 0
      // Value might be blank when user is previewing settings
      const maxValue = rootGetters.reactionData.max_value || 0
      // Avoid out-of-memory due to too many <option>s
      return Math.min(maxValue, 100)
    },
    myReaction(state, getters, rootState): Reaction {
      const myReactions = getters.reactions.filter(
        (reaction: Reaction): boolean =>
          reaction.user_id === rootState.user.id && reaction.wish_id === state.postIdBeingReactedTo,
      )
      return myReactions.length > 0 ? myReactions[0] : null
    },
    xReactionDropdownStar(state, getters, rootState, rootGetters): boolean {
      return !!state.postIdBeingReactedTo && rootGetters.reactionData.type === 'star'
    },
    xReactionDropdownGrade(state, getters, rootState, rootGetters): boolean {
      return !!state.postIdBeingReactedTo && rootGetters.reactionData.type === 'grade'
    },
    postIdBeingReactedTo: (state): Id | null => state.postIdBeingReactedTo,
    isIntialReactionsLoadDone: (state): boolean => state.isIntialReactionsLoadDone,
    reactionEntities: (state): { [id: string]: Reaction } => state.reactionEntities,
    accumulatedReactionsByWishId: (state): AccumulatedReactions => state.accumulatedReactionsByWishId,
  },
  mutations: {
    FETCH_REACTIONS_SUCCESS(state, newReactionEntities): void {
      state.reactionEntities = newReactionEntities || {}
    },
    FETCH_INITIAL_PAGINATED_REACTIONS_DONE(state): void {
      state.isIntialReactionsLoadDone = true
      state.pendingReactionIds = []
    },
    UPDATE_REACTION(state, { id, obj }): void {
      if (id < 0 || !obj) {
        return
      }
      const updatedEntity = {
        ...state.reactionEntities[id],
        ...obj,
      }
      state.reactionEntities = {
        ...state.reactionEntities,
        [id]: updatedEntity,
      }
    },
    ADD_PENDING_REACTION(state, pendingReaction): void {
      const isPendingReactionAlreadyThere = state.pendingReactionIds.some((id: ReactionId): boolean => {
        if (
          state.reactionEntities[id] &&
          state.reactionEntities[id].wish_id === pendingReaction.wish_id &&
          state.reactionEntities[id].value === pendingReaction.value
        ) {
          return true
        }
        return false
      })
      if (!isPendingReactionAlreadyThere) {
        const tempId = 'new_' + new Date().valueOf()
        state.reactionEntities = {
          ...state.reactionEntities,
          [tempId]: pendingReaction,
        }
        state.pendingReactionIds.push(tempId)
      }
    },
    ADD_REACTION(state, reaction): void {
      const pendingReactionIdRemoved = state.pendingReactionIds.some((id: ReactionId): boolean => {
        if (
          state.reactionEntities[id].wish_id === reaction.wish_id &&
          state.reactionEntities[id].value === reaction.value &&
          state.reactionEntities[id].user_id === reaction.user_id &&
          state.reactionEntities[id].reaction_type === reaction.reaction_type
        ) {
          const newReactionEntities = {
            ...state.reactionEntities,
          }
          delete newReactionEntities[id]
          state.reactionEntities = {
            ...newReactionEntities,
            [reaction.id]: reaction,
          }
          state.pendingReactionIds = state.pendingReactionIds.filter((i: ReactionId): boolean => i !== id)
          return true
        }
        return false
      })
      if (!pendingReactionIdRemoved) {
        state.reactionEntities = {
          ...state.reactionEntities,
          [reaction.id]: reaction,
        }
      }
    },
    REMOVE_REACTION(state, id): void {
      const newReactionEntities = { ...state.reactionEntities }
      delete newReactionEntities[id]
      state.reactionEntities = newReactionEntities
    },
    EDIT_REACTION_REMOTE(state, reaction): void {
      const originalReaction = state.reactionEntities[reaction.id]
      // real time can be out of sync sometimes. here we make sure we are not updating a new copy with a stale one
      if (originalReaction && originalReaction.updated_at <= reaction.updated_at) {
        state.reactionEntities = {
          ...state.reactionEntities,
          [reaction.id]: reaction,
        }
      }
    },
    START_EDITING_REACTION(state, { postId, dropdownTop, dropdownLeft, dropdownWidth }): void {
      state.postIdBeingReactedTo = postId
      state.reactionDropdownTop = dropdownTop
      state.reactionDropdownLeft = dropdownLeft
      state.reactionDropdownWidth = dropdownWidth
    },
    STOP_EDITING_REACTION(state): void {
      state.postIdBeingReactedTo = null
    },
    SET_ACCUMULATED_REACTIONS_BY_WISH_ID(state, accumulatedReactionsByWishId: AccumulatedReactions): void {
      state.accumulatedReactionsByWishId = accumulatedReactionsByWishId
    },
  },
  actions: {
    // shouldResetState is a flag to reset the stored reaction data
    // currently it is only set to true for the initial reaction load when a Padlet page is first loaded
    fetchReactions({ dispatch, rootGetters }, { shouldResetState }): void {
      PadletApi.Reaction.fetch({ wallId: rootGetters.wallId })
        .then((fetchedData: JsonAPIResponse<Reaction>): void => {
          const { data, meta } = fetchedData
          const nextPageCursor = meta?.next
          dispatch('fetchReactionsSuccess', {
            newReactions: data,
            isFirstPage: true,
            isLastPage: !nextPageCursor,
            shouldResetState,
          })

          if (nextPageCursor) {
            dispatch('fetchReactionsForPage', { pageStart: nextPageCursor })
          }
        })
        .catch((e): void => {
          // Don't capture exception when it's 401, show a snackbar instead
          if (e.status === 401) {
            dispatch('snackbars/setSnackbar', ACCESS_WITHDRAWN, { root: true })
          } else {
            captureFetchException(e, { source: 'fetchReactions' })
          }
        })
    },
    fetchReactionsForPage({ dispatch, rootGetters }, { pageStart }): void {
      PadletApi.Reaction.fetch({ wallId: rootGetters.wallId, pageStart })
        .then((reacionPage: JsonAPIResponse<Reaction>) => {
          const { data, meta } = reacionPage
          const nextPageCursor = meta?.next
          dispatch('fetchReactionsSuccess', {
            newReactions: data,
            isFirstPage: false,
            isLastPage: !nextPageCursor,
            shouldResetState: false,
          })
          if (nextPageCursor) {
            dispatch('fetchReactionsForPage', { pageStart: nextPageCursor })
          }
        })
        .catch((e) => {
          captureFetchException(e, { source: 'fetchReactionsForPage' })
        })
    },
    fetchReactionsSuccess(context, { newReactions, isFirstPage, isLastPage, shouldResetState }): void {
      const newReactionEntities = reconcileReactionsAfterFetch({
        newReactions,
        context,
        isFirstPage,
        isLastPage,
        shouldResetState,
      })
      const { commit, dispatch } = context
      commit('FETCH_REACTIONS_SUCCESS', newReactionEntities)
      if (isLastPage) {
        commit('FETCH_INITIAL_PAGINATED_REACTIONS_DONE')
        void useNativeAppStore().postSurfaceState()
      }
    },
    createReaction({ commit, state, getters, rootState, rootGetters, dispatch }, { postId, value }): void {
      const existingReaction = getters.reactions.find(
        (reaction: Reaction): boolean =>
          reaction.user_id === rootState.user.id &&
          reaction.wish_id === postId &&
          reaction.reaction_type === rootGetters.reactionData.type,
      )
      if (existingReaction) {
        commit('UPDATE_REACTION', {
          id: existingReaction.id,
          obj: {
            value,
          },
        })
        PadletApi.Reaction.update({ ...state.reactionEntities[existingReaction.id], value })
      } else {
        const newReaction = {
          wish_id: postId,
          value,
          reaction_type: rootGetters.reactionData.type,
        }
        // not handling pending reaction for now, request is quick enough
        commit('ADD_PENDING_REACTION', {
          ...newReaction,
          user_id: rootState.user.id,
        })
        PadletApi.Reaction.create(newReaction).then((reactionData: JsonAPIResponse<Reaction>): void => {
          dispatch('createReactionSuccess', { reaction: (reactionData.data as JsonAPIResource<Reaction>).attributes })
        })
      }
      dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
    },
    createReactionSuccess({ commit }, { reaction }): void {
      commit('ADD_REACTION', reaction)
    },
    deleteReaction({ commit, getters, rootState }, { postId }): void {
      const existingReaction = getters.reactions.find(
        (reaction: Reaction): boolean => reaction.user_id === rootState.user.id && reaction.wish_id === postId,
      )
      commit('STOP_EDITING_REACTION')
      if (existingReaction) {
        commit('REMOVE_REACTION', existingReaction.id)
        PadletApi.Reaction.delete(existingReaction)
      }
    },
    startEditingReaction({ commit, rootGetters, dispatch }, payload): void {
      if (rootGetters.isApp && appCan('useNativeReactionDialog')) {
        // update surface state right before editing to make sure reaction data is up to date
        void useNativeAppStore().postSurfaceState()
        postMessage(openPostReactionDialog(payload.postId))
      } else {
        commit('START_EDITING_REACTION', payload)
      }
    },
    stopEditingReaction({ commit }): void {
      commit('STOP_EDITING_REACTION')
    },
    newReactionRemote({ commit, dispatch }, payload): void {
      commit('ADD_REACTION', payload)
      void useNativeAppStore().postSurfaceState()
    },
    editReactionRemote({ commit, dispatch }, payload): void {
      commit('EDIT_REACTION_REMOTE', payload)
      void useNativeAppStore().postSurfaceState()
    },
    deleteReactionRemote({ commit, state, dispatch }, reaction): void {
      if (state.postIdBeingReactedTo === reaction.wish_id) {
        commit('STOP_EDITING_REACTION')
      }
      commit('REMOVE_REACTION', reaction.id)
      void useNativeAppStore().postSurfaceState()
    },
    refreshAccumulatedReactions({ commit }, data: AccumulatedReactions): void {
      commit('SET_ACCUMULATED_REACTIONS_BY_WISH_ID', data ?? {})
    },
  },
}

export default ReactionModule
export { ReactionState }
